import React, { useMemo, useState } from 'react';
import { Box, Button, Flex, Text, useColorMode } from 'theme-ui';
import { FiCopy, FiEdit2, FiPaperclip, FiTrash2 } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { FaFileAlt } from 'react-icons/fa';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import {
  IDeliveryNoteRow,
  ISheetIndexItem,
  SheetData,
  BaseDeliveryTableProps,
  DeliveryNotesTableProps,
  SheetIndexTableProps,
  DeliveryTableProps,
} from './types';
import { getFullUrl } from '../../../utils/global';
import { uploadIcon } from '../../../helper/uploadType';
import { downloadAttachments, InlineEditCell } from '../../../utils/GenerateQueryString';
import { linkifyText } from '../../../utils/textFormat';
import { BorderedGridColumn, BorderedGridTable } from './BorderedGridTable';
import { parseInput } from './deliveryModalUtils';

export type {
  BaseDeliveryTableProps,
  DeliveryNotesTableProps,
  SheetIndexTableProps,
  DeliveryTableProps,
};

const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
};

const formatNormalText = (str: string): string => {
  if (!str) return '-';
  if (str === str.toUpperCase()) {
    return str
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  return str;
};

export const DeliveryTable: React.FC<DeliveryTableProps> = (props) => {
  const {
    cardBg,
    sectionBg,
    borderColor,
    textColor,
    subTextColor,
    primaryColor,
    height = '220px',
    isCompletedTab = false,
  } = props;

  const [colorMode] = useColorMode();
  const isDark = colorMode === 'dark';
  const modalBg = isDark ? '#20283c' : '#ffffff';
  const titleColor = isDark ? '#ffffff' : '#0f172a';

  // Attachment Modal State (for delivery notes)
  const [attachmentModal, setAttachmentModal] = useState<{
    open: boolean;
    attachments: string[];
  }>({
    open: false,
    attachments: [],
  });
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);

  // Delivery Notes Columns
  const deliveryNotesColumns = useMemo<BorderedGridColumn<IDeliveryNoteRow>[]>(() => {
    if (props.type !== 'deliveryNotes') return [];
    const { onToggleInclude, onEditNote } = props;

    return [
      {
        id: 'stageRevPc',
        header: 'Stage/Rev/PC',
        align: 'center',
        px: '4px',
        cell: (row) => (
          <Text sx={{ textAlign: 'center', fontWeight: 600 }}>{row.revision || '—'}</Text>
        ),
      },
      {
        id: 'addedBy',
        header: 'Added By',
        align: 'center',
        px: '4px',
        cell: (row) => (
          <Text
            title={row.addedBy}
            sx={{
              textAlign: 'center',
              color: subTextColor,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
            }}
          >
            {row.addedBy || '—'}
          </Text>
        ),
      },
      {
        id: 'date',
        header: 'Date',
        align: 'center',
        px: '4px',
        cell: (row) => (
          <Text sx={{ textAlign: 'center', color: subTextColor, whiteSpace: 'nowrap' }}>
            {row.date || '—'}
          </Text>
        ),
      },
      {
        id: 'deliveryNote',
        header: 'Delivery Note',
        align: 'center',
        px: '8px',
        cell: (row) => {
          if (row.id === editingNoteId) {
            return (
              <InlineEditCell
                initialValue={row.note}
                onSave={(newText: string) => {
                  const trimmed = newText.trim();
                  if (!trimmed) return;
                  setEditingNoteId(null);
                  if (trimmed !== row.note) onEditNote(row.id, trimmed);
                }}
                onCancel={() => setEditingNoteId(null)}
                borderColor={borderColor}
                colorMode={colorMode}
                textColor={textColor}
              />
            );
          }
          return (
            <Box sx={{ width: '100%', wordBreak: 'break-word', overflow: 'hidden' }}>
              <Text sx={{ fontSize: '11px', color: textColor, lineHeight: 1.3, whiteSpace: 'pre-line' }}>
                {linkifyText(row.note, primaryColor)}
              </Text>
            </Box>
          );
        },
      },
      {
        id: 'include',
        header: 'Include',
        align: 'center',
        px: '4px',
        cell: (row) => (
          <input
            type="checkbox"
            checked={row.include}
            disabled={isCompletedTab}
            onChange={() => !isCompletedTab && onToggleInclude(row.id)}
            style={{ accentColor: primaryColor, cursor: isCompletedTab ? 'default' : 'pointer' }}
          />
        ),
      },
      {
        id: 'attach',
        header: 'Attach',
        align: 'center',
        px: '4px',
        cell: (row) => {
          const attList: string[] = [];
          if (Array.isArray(row.attachment)) {
            row.attachment.forEach((a) => {
              if (typeof a === 'string' && a.trim()) {
                attList.push(a.trim());
              }
            });
          } else if (typeof row.attachment === 'string' && (row.attachment as string).trim()) {
            attList.push((row.attachment as string).trim());
          }

          if (attList.length === 0) {
            return <Text sx={{ color: subTextColor, fontSize: '11px' }}>-</Text>;
          }

          if (attList.length === 1) {
            const fullUrl = getFullUrl(attList[0]);
            const fileName = attList[0].split('/').pop() || attList[0];
            return (
              <a
                href={fullUrl}
                target="_blank"
                rel="noopener noreferrer"
                download
                title={fileName}
                style={{ color: primaryColor, lineHeight: 1, display: 'inline-flex', alignItems: 'center', gap: '3px', textDecoration: 'none', }}>
                <FiPaperclip size={13} />
                <Text sx={{ fontSize: '11px', fontWeight: 600, color: primaryColor }}>
                  {attList.length}
                </Text>
              </a>
            );
          }

          return (
            <Box
              onClick={() => {
                setAttachmentModal({ open: true, attachments: attList });
                setSelectedIndices([]);
              }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px',
                cursor: 'pointer',
                color: primaryColor,
                lineHeight: 1,
              }}
              title={`View ${attList.length} attachments`}
            >
              <FiPaperclip size={13} />
              <Text sx={{ fontSize: '11px', fontWeight: 600, color: primaryColor }}>
                {attList.length}
              </Text>
            </Box>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        align: 'center',
        px: '4px',
        cell: (row) => {
          const isEditing = row.id === editingNoteId;
          return (
            <Flex sx={{ gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
              {!isCompletedTab && (
                <Box
                  onClick={() => setEditingNoteId(isEditing ? null : row.id)}
                  sx={{
                    cursor: 'pointer',
                    color: isEditing ? primaryColor : subTextColor,
                    '&:hover': { color: primaryColor },
                  }}
                  title="Edit note"
                >
                  <FiEdit2 size={13} />
                </Box>
              )}
              <Box
                onClick={async () => {
                  const ok = await copyToClipboard(row.note || '');
                  if (ok) toast.success('Copied to clipboard');
                  else toast.error('Failed to copy');
                }}
                sx={{
                  cursor: 'pointer',
                  color: subTextColor,
                  '&:hover': { color: primaryColor },
                }}
                title="Copy note"
              >
                <FiCopy size={13} />
              </Box>
            </Flex>
          );
        },
      },
    ];
  }, [props, subTextColor, textColor, isCompletedTab, primaryColor, editingNoteId, borderColor, colorMode]);

  // Sheet Index Columns
  const sheetIndexColumns = useMemo<BorderedGridColumn<ISheetIndexItem>[]>(() => {
    if (props.type !== 'sheetIndex') return [];
    const { onStartEdit, onDeleteSheet } = props;

    const cols: BorderedGridColumn<ISheetIndexItem>[] = [
      {
        id: 'sheetNo',
        header: 'Sheet No.',
        align: 'center',
        px: '4px',
        cell: (item) => {
          const sNo = item.sheetNo || item.name?.split('-')[0]?.trim() || '-';
          const isUpdated = (item.status || 'UPDATED').trim().toUpperCase() === 'UPDATED';
          return (
            <Text sx={{ fontWeight: isUpdated ? 600 : 400, whiteSpace: 'nowrap', textAlign: 'center' }}>
              {sNo}
            </Text>
          );
        },
      },
      {
        id: 'sheetTitle',
        header: 'Sheet Title',
        align: 'center',
        px: '8px',
        cell: (item) => {
          const rawTitle = item.sheetTitle || item.name || '-';
          const displayTitle = formatNormalText(rawTitle);
          const isUpdated = (item.status || 'UPDATED').trim().toUpperCase() === 'UPDATED';
          return (
            <Text
              title={displayTitle}
              sx={{
                fontWeight: isUpdated ? 600 : 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              {displayTitle}
            </Text>
          );
        },
      },
      {
        id: 'discipline',
        header: 'Discipline',
        align: 'center',
        px: '4px',
        cell: (item) => {
          const rawDisc = item.discipline || '-';
          const displayDisc = formatNormalText(rawDisc);
          return (
            <Text
              title={displayDisc}
              sx={{
                color: subTextColor,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: 'center',
                width: '100%',
              }}
            >
              {displayDisc}
            </Text>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        align: 'center',
        px: '4px',
        cell: (item) => {
          const rawStatus = (item.status || 'UPDATED').trim();
          const isUpdated = rawStatus.toUpperCase() === 'UPDATED';
          return (
            <Box
              as="span"
              sx={{
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: isUpdated ? 600 : 500,
                backgroundColor: isUpdated ? '#DCFCE7' : '#F1F5F9',
                color: isUpdated ? '#15803D' : '#64748B',
                border: `1px solid ${isUpdated ? '#86EFAC' : '#E2E8F0'}`,
                whiteSpace: 'nowrap',
              }}
            >
              {isUpdated ? 'Updated' : 'Unchanged'}
            </Box>
          );
        },
      },
    ];

    if (!isCompletedTab) {
      cols.push({
        id: 'actions',
        header: 'Actions',
        align: 'center',
        px: '4px',
        cell: (item) => (
          <Flex sx={{ gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
            <Box
              onClick={() => onStartEdit(item)}
              sx={{
                cursor: 'pointer',
                color: subTextColor,
                '&:hover': { color: primaryColor },
              }}
              title="Edit sheet"
            >
              <FiEdit2 size={13} />
            </Box>
            <Box
              onClick={() => onDeleteSheet(item.id)}
              sx={{
                cursor: 'pointer',
                color: '#EF4444',
                '&:hover': { opacity: 0.8 },
              }}
              title="Delete sheet"
            >
              <FiTrash2 size={13} />
            </Box>
          </Flex>
        ),
      });
    }

    return cols;
  }, [props, isCompletedTab, subTextColor, primaryColor]);

  // Render Delivery Notes variant
  if (props.type === 'deliveryNotes') {
    const { notes, activeTrade, onTradeChange, trades: propsTrades, isLoading = false, title = 'Delivery Notes By Trade' } = props;
    const dynamicTrades = Array.from(
      new Set(
        notes
          .map((n) => n.trade)
          .filter((t): t is string => Boolean(t) && t.trim() !== '' && t.toUpperCase() !== 'PM')
      )
    );
    const resolvedTrades = (propsTrades && propsTrades.length > 0 ? propsTrades : dynamicTrades)
      .filter((t) => Boolean(t) && t.trim() !== '' && t.toUpperCase() !== 'PM');
    const trades = resolvedTrades.length > 0 ? resolvedTrades : [];
    const selectedTrade = trades.includes(activeTrade) ? activeTrade : trades[0] || '';
    const filteredTradeNotes = notes
      .filter((item) => item.trade === selectedTrade)
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

    return (
      <>
        <BorderedGridTable
          title={title}
          tabs={{
            list: trades,
            active: selectedTrade,
            onChange: onTradeChange,
          }}
          isLoading={isLoading}
          columns={deliveryNotesColumns}
          data={filteredTradeNotes}
          gridTemplateColumns="95px 100px 80px 1fr 55px 50px 64px"
          keyExtractor={(item) => item.id}
          height={height}
          borderColor={borderColor}
          sectionBg={sectionBg}
          textColor={textColor}
          subTextColor={subTextColor}
          primaryColor={primaryColor}
          emptyText="No delivery notes added yet"
        />

        {/* Manage Attachments Modal */}
        {attachmentModal.open && (
          <Modal
            isOpen={attachmentModal.open}
            toggle={() => {
              setAttachmentModal({ open: false, attachments: [] });
              setSelectedIndices([]);
            }}
            centered
            size="md"
          >
            <ModalHeader
              toggle={() => {
                setAttachmentModal({ open: false, attachments: [] });
                setSelectedIndices([]);
              }}
              style={{ backgroundColor: modalBg, color: titleColor }}
            >
              Manage Attachments
            </ModalHeader>
            <ModalBody style={{ backgroundColor: modalBg, color: titleColor, padding: '24px' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {attachmentModal.attachments.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Flex sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text sx={{ fontWeight: 600, fontSize: '14px', color: titleColor }}>Current Attachments:</Text>
                      {selectedIndices.length > 0 && (
                        <Button
                          type="button"
                          disabled={isDownloading}
                          onClick={async () => {
                            const urls = selectedIndices.map((i) => attachmentModal.attachments[i]).filter(Boolean);
                            if (!urls.length) return;
                            setIsDownloading(true);
                            try {
                              await downloadAttachments(urls);
                            } catch {
                              /* ignore */
                            } finally {
                              setIsDownloading(false);
                            }
                          }}
                          sx={{
                            fontSize: '12px',
                            padding: '4px 10px',
                            backgroundColor: '#7743DB',
                            color: '#ffffff',
                            borderRadius: '4px',
                            cursor: isDownloading ? 'not-allowed' : 'pointer',
                            border: 'none',
                            opacity: isDownloading ? 0.7 : 1,
                          }}
                        >
                          {isDownloading ? 'Downloading...' : selectedIndices.length === 1 ? 'Download' : 'Download All'}
                        </Button>
                      )}
                    </Flex>

                    {selectedIndices.length === 0 && (
                      <Text sx={{ fontSize: '13px', color: '#ef4444', fontStyle: 'italic', marginTop: '-4px' }}>
                        * Please select checkbox to download attachments
                      </Text>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '240px', overflowY: 'auto', padding: '2px' }}>
                      {attachmentModal.attachments.map((url, idx) => {
                        const parts = url.split('/');
                        const filename = parts[parts.length - 1];
                        const ext = filename.split('.').pop()?.toLowerCase() || '';
                        const iconSrc = uploadIcon(ext);
                        const fullUrl = getFullUrl(url);

                        return (
                          <Flex
                            key={idx}
                            sx={{
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              border: isDark ? '1px solid #374151' : '1px solid #E2E8F0',
                              backgroundColor: isDark ? 'rgba(13,202,240,0.12)' : 'rgba(248,249,250,1)',
                              gap: '12px',
                            }}
                          >
                            <Flex sx={{ alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
                              <input
                                type="checkbox"
                                checked={selectedIndices.includes(idx)}
                                onChange={() =>
                                  setSelectedIndices((prev) =>
                                    prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
                                  )
                                }
                                style={{ cursor: 'pointer', flexShrink: 0, width: '16px', height: '16px', accentColor: '#7743DB' }}
                              />
                              {iconSrc ? (
                                <img src={iconSrc} alt={ext} style={{ width: '22px', height: '22px', objectFit: 'contain', flexShrink: 0 }} />
                              ) : (
                                <FaFileAlt size={20} color="#0dcaf0" style={{ flexShrink: 0 }} />
                              )}
                              <a
                                href={fullUrl}
                                target="_blank"
                                rel="noreferrer"
                                title={filename}
                                style={{
                                  fontSize: '13px',
                                  fontWeight: 500,
                                  color: '#0dcaf0',
                                  textDecoration: 'none',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: '280px',
                                  display: 'inline-block',
                                }}
                              >
                                {`Link ${idx + 1}`}
                              </a>
                            </Flex>
                          </Flex>
                        );
                      })}
                    </Box>
                  </Box>
                ) : (
                  <Text sx={{ fontSize: '14px', color: '#8F9195' }}>No attachments available.</Text>
                )}

                <Flex sx={{ justifyContent: 'flex-end', pt: 2 }}>
                  <Button
                    onClick={() => {
                      setAttachmentModal({ open: false, attachments: [] });
                      setSelectedIndices([]);
                    }}
                    sx={{
                      backgroundColor: '#ffffff',
                      color: '#333333',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      px: '20px',
                      py: '6px',
                      fontSize: '13px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f3f4f6' },
                    }}
                  >
                    Cancel
                  </Button>
                </Flex>
              </Box>
            </ModalBody>
          </Modal>
        )}
      </>
    );
  }

  // Render Sheet Index variant
  const {
    sheetIndexes,
    onAddClick,
    showAddSheet,
    sheetTextarea,
    onSheetTextareaChange,
    sheetError,
    onAddSubmit,
    onCancelAdd,
    editingSheetId,
  } = props;

  const sheetGridTemplate = isCompletedTab
    ? '85px 1.25fr 1fr 95px'
    : '80px 1.2fr 1fr 90px 55px';

  const trimmedInput = (sheetTextarea || '').trim();
  let previewSheets: SheetData[] = [];
  if (trimmedInput && trimmedInput.replace(/[\s#]/g, '')) {
    const res = parseInput(trimmedInput, true);
    if (res.length > 0 && typeof res[0] !== 'string') {
      previewSheets = res as SheetData[];
    }
  }

  const liveError = editingSheetId && previewSheets.length > 1 ? 'Only 1 sheet is allowed while updating' : null;
  const activeError = sheetError || liveError;

  return (
    <>
      <BorderedGridTable
        title="Sheet Index"
        subtitle="(updated sheets only)"
        actionText={!isCompletedTab ? '+ Upload Sheet' : null}
        onActionClick={onAddClick}
        columns={sheetIndexColumns}
        data={sheetIndexes}
        gridTemplateColumns={sheetGridTemplate}
        keyExtractor={(item) => item.id}
        height={height}
        borderColor={borderColor}
        sectionBg={sectionBg}
        textColor={textColor}
        subTextColor={subTextColor}
        primaryColor={primaryColor}
        emptyText="No sheets added yet"
      />

      {/* Upload / Edit Sheet Index Modal */}
      {!isCompletedTab && showAddSheet && (
        <Modal isOpen={showAddSheet} toggle={onCancelAdd} centered size="lg">
          <ModalHeader toggle={onCancelAdd} style={{ backgroundColor: modalBg, color: titleColor }}>
            {editingSheetId ? 'Edit Sheet' : 'Add New Sheet'}
          </ModalHeader>
          <ModalBody style={{ backgroundColor: modalBg, color: titleColor, padding: '20px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <Box>
                <Text sx={{ fontSize: '13px', fontWeight: 600, color: titleColor, mb: '4px' }}>
                  Enter sheet data in format:
                </Text>
                <Text sx={{ fontSize: '12px', ml: '2px', color: primaryColor, fontFamily: 'monospace', fontWeight: 500 }}>
                  #Sheet No - Sheet Title - Discipline - Status;
                </Text>
              </Box>

              <textarea
                rows={4}
                placeholder={`#M001 - MECHANICAL SCHEDULES & NOTES - MECHANICAL - UPDATED\n#P001 - PLUMBING PLAN - PLUMBING - UNCHANGED`}
                value={sheetTextarea}
                onChange={(e) => onSheetTextareaChange(e.target.value)}
                style={{
                  width: '100%',
                  fontSize: '12px',
                  padding: '8px 10px',
                  borderRadius: '6px',
                  border: `1px solid ${activeError ? '#EF4444' : borderColor}`,
                  backgroundColor: sectionBg,
                  color: textColor,
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  outline: 'none',
                }}
              />

              {activeError && (
                <Box sx={{ color: '#EF4444', fontSize: '12px', whiteSpace: 'pre-wrap', backgroundColor: 'rgba(239, 68, 68, 0.1)', padding: '8px 12px', borderRadius: '6px' }}>
                  {activeError}
                </Box>
              )}

              {/* Live Preview */}
              {previewSheets.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Text sx={{ fontSize: '12px', fontWeight: 600, color: titleColor }}>
                    Preview ({previewSheets.length} {previewSheets.length === 1 ? 'sheet' : 'sheets'} parsed):
                  </Text>
                  <Box
                    sx={{
                      maxHeight: '180px',
                      overflowY: 'auto',
                      borderRadius: '6px',
                      border: `1px solid ${borderColor}`,
                    }}
                  >
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#F3F4F6', color: subTextColor, textAlign: 'left' }}>
                          <th style={{ padding: '6px 10px' }}>Sheet No.</th>
                          <th style={{ padding: '6px 10px' }}>Sheet Title</th>
                          <th style={{ padding: '6px 10px' }}>Discipline</th>
                          <th style={{ padding: '6px 10px' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewSheets.map((s, idx) => (
                          <tr
                            key={idx}
                            style={{
                              borderTop: `1px solid ${borderColor}`,
                              color: textColor,
                            }}
                          >
                            <td style={{ padding: '6px 10px', fontWeight: 600 }}>{s.sheetNo}</td>
                            <td style={{ padding: '6px 10px' }}>{s.sheetTitle}</td>
                            <td style={{ padding: '6px 10px' }}>{s.discipline}</td>
                            <td style={{ padding: '6px 10px' }}>
                              <span
                                style={{
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  backgroundColor: s.status.trim().toUpperCase() === 'UPDATED'
                                    ? (isDark ? 'rgba(34,197,94,0.2)' : '#DCFCE7')
                                    : (isDark ? 'rgba(156,163,175,0.2)' : '#F3F4F6'),
                                  color: s.status.trim().toUpperCase() === 'UPDATED' ? '#16A34A' : subTextColor,
                                }}
                              >
                                {s.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              )}

              <Flex sx={{ justifyContent: 'flex-end', gap: '8px', mt: '8px' }}>
                <Button
                  type="button"
                  variant="CancelButton"
                  onClick={onCancelAdd}
                  sx={{
                    fontSize: '12px',
                    padding: '6px 14px',
                    cursor: 'pointer',
                    backgroundColor: isDark ? '#374151' : '#E5E7EB',
                    color: textColor,
                    border: 'none',
                    borderRadius: '6px',
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={onAddSubmit}
                  disabled={Boolean(activeError)}
                  sx={{
                    fontSize: '12px',
                    padding: '6px 16px',
                    cursor: activeError ? 'not-allowed' : 'pointer',
                    opacity: activeError ? 0.6 : 1,
                    backgroundColor: primaryColor,
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: 600,
                  }}
                >
                  {editingSheetId ? 'Update Sheet' : 'Add Sheets'}
                </Button>
              </Flex>
            </Box>
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

export default DeliveryTable;
